import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowDownToLine, ArrowLeft, ArrowRight, LoaderCircle, Pause } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Loading, Modal, Status } from '../components/common';
import { useLab } from '../lib/service';
import { episodeAllowance } from '../../../../packages/domain/src/production-plan';
import { MODELS, type CommandKind } from '@drama/domain';
import { isBibleKind } from '../../../../packages/domain/src/view';
import { activeStatuses } from '../shared';
import {
  productionWorkMessage,
  seriesProductionState,
  seriesRequestInput,
} from '../lib/production-state';
import { SeriesProvider, type SeriesValue } from './context';
import { OverviewTab } from './OverviewTab';
import { BlueprintTab } from './BlueprintTab';
import { CastTab } from './CastTab';
import { EpisodesTab } from './EpisodesTab';
import { HistoryTab } from './HistoryTab';
import { ShotModal } from './ShotModal';
import { KidsPanel } from '../kids/KidsPanel';

/**
 * Container for the series screen.
 *
 * Owns the tab selection and every derived value the tabs share, and publishes them
 * through `SeriesProvider`. The five tab bodies, the shot modal, the review form and the
 * reconciliation control live in sibling modules; this file was previously an 867-line
 * function inside a 2,192-line `App.tsx`.
 */
export function Series({ onError }: { onError: (e: unknown) => void }) {
  const lab = useLab(),
    d = lab.detail;
  const [tab, setTab] = useState('Overview'),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(''),
    [selectedEpisode, setSelectedEpisode] = useState(''),
    [selectedShot, setSelectedShot] = useState<string>(),
    [model, setModel] = useState<string>(MODELS.video),
    [direction, setDirection] = useState(''),
    [showExport, setShowExport] = useState(false),
    [narration, setNarration] = useState('');
  if (!d) return <Loading />;
  const p = d.project;
  const pitch = d.bibles.filter(isBibleKind('pitch')).at(-1)?.data;
  const ep = d.episodes.find((e) => e._id === selectedEpisode) ?? d.episodes[0];
  const shots = d.shots
    .filter((s) => s.episodeId === ep?._id && s.scriptVersion === ep.scriptVersion)
    .sort((a, b) => a.spec.order - b.spec.order);
  const managed = d.managedProviderProfile !== undefined;
  const profile = lab.home?.providerProfiles?.find((x) => x.id === d.managedProviderProfile);
  const effectiveModel = profile?.steps.VIDEO?.model ?? model;
  const { writable, running, available, controlsDisabled, generationUnavailable } =
    seriesProductionState(lab.home, d, busy);
  async function run(kind: CommandKind, extra: Record<string, string | number | boolean> = {}) {
    setBusy(true);
    try {
      if (generationUnavailable) throw new Error(generationUnavailable);
      if (controlsDisabled)
        throw new Error('Finish the current work or check your editing access.');
      await lab.request(
        seriesRequestInput(p._id, ep?._id, kind, profile, {
          ...(profile ? {} : { videoModel: model }),
          ...extra,
        }),
      );
      setRevision('');
      return true;
    } catch (e) {
      onError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  const plan = episodeAllowance(
    shots,
    d.versions,
    d.assets,
    p.seasonVersion,
    effectiveModel,
    d.plates,
    profile,
  );
  const active = d.commands.filter((c) => activeStatuses.includes(c.status)).at(-1);
  const needsAttention =
    active?.status === 'RECONCILIATION' ||
    active?.status === 'PAUSED_BUDGET' ||
    active?.status === 'PAUSED_QUOTA';
  const workMessage = productionWorkMessage(active, lab.home, d.jobs);
  const nextEpisode = ep && d.episodes.find((e) => e.number > ep.number && e.status !== 'ACCEPTED');
  const guide = !pitch
    ? {
        title: 'Develop your idea',
        text: 'Turn your premise into a pitch and characters.',
        tab: 'Overview',
      }
    : !p.seasonVersion
      ? {
          title: 'Review your series direction',
          text: 'Adjust the pitch, then approve a season plan. Video starts only when requested.',
          tab: 'Overview',
        }
      : !p.castLocked
        ? {
            title: 'Choose and lock your cast',
            text: 'Review the character references once; reuse their identities throughout the series.',
            tab: 'Cast',
          }
        : !ep?.scriptVersion
          ? {
              title: 'Prepare this episode',
              text: 'Create its screenplay, then review what will be produced.',
              tab: 'Episodes',
            }
          : ep.status === 'ACCEPTED'
            ? {
                title: nextEpisode ? 'Continue your series' : 'Your episode is approved',
                text: nextEpisode
                  ? 'Choose the next episode when you are ready. Nothing starts automatically.'
                  : 'Your accepted output is preserved and ready to download.',
                tab: 'Episodes',
              }
            : ep.currentMasterId
              ? {
                  title: 'Watch and review',
                  text: 'Review the complete episode, describe a change, or approve this version and its shots together.',
                  tab: 'Episodes',
                }
              : {
                  title: plan.reusedShots ? 'Finish your episode' : 'Produce your episode',
                  text: 'Review the allowance below. Passed shots are reused; captions and assembly finish automatically.',
                  tab: 'Episodes',
                };

  const value: SeriesValue = {
    detail: d,
    project: p,
    pitch,
    episode: ep,
    shots,
    plan,
    running,
    writable,
    controlsDisabled,
    busy,
    available,
    model,
    effectiveModel,
    providerProfile: profile,
    setModel,
    revision,
    setRevision,
    selectedShot,
    setSelectedShot,
    setSelectedEpisode: (episodeId) => {
      setSelectedShot(undefined);
      setSelectedEpisode(episodeId);
    },
    setTab,
    narration,
    setNarration,
    direction,
    setDirection,
    run,
    act,
    onError,
  };

  return (
    <SeriesProvider value={value}>
      <Link to="/" className="back-link">
        <ArrowLeft size={15} />
        All series
      </Link>
      <div className="series-page-heading">
        <div>
          <div className="eyebrow">
            {p.genre.toUpperCase()}
            <span> / </span>SEASON 01
          </div>
          <h1>{p.title}</h1>
          <p>{pitch?.synopsis ?? p.idea}</p>
        </div>
        <div className="series-heading-actions">
          <Status value={p.phase} />
          <Button variant="outline" onClick={() => setShowExport(true)}>
            <ArrowDownToLine size={16} />
            Production notes
          </Button>
        </div>
      </div>
      {p.kids && !lab.isDemo && (
        <KidsPanel projectId={p._id} episodes={d.episodes} writable={writable} onError={onError} />
      )}
      {lab.home?.allowance && (
        <p>
          My production allowance: $
          {(
            (lab.home.allowance.cap - lab.home.allowance.spent - lab.home.allowance.reserved) /
            1_000_000
          ).toFixed(2)}{' '}
          available of ${(lab.home.allowance.cap / 1_000_000).toFixed(2)}.
        </p>
      )}
      {managed && (
        <p>Production profile: {profile?.id ?? d.managedProviderProfile}. Set by the Lab owner.</p>
      )}
      {generationUnavailable && <p role="status">{generationUnavailable}</p>}
      {!(tab === 'Episodes' && ep?.currentMasterId && !running) && (
        <section className="next-step" aria-label="Next step">
          <div>
            <span className="eyebrow">{running ? 'IN PROGRESS' : 'UP NEXT'}</span>
            <h2>{running ? workMessage : guide.title}</h2>
            <p>
              {running
                ? 'Your work is saved. You can leave this page and return later.'
                : guide.text}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              if (!running && ep?.status === 'ACCEPTED' && nextEpisode)
                setSelectedEpisode(nextEpisode._id);
              setTab(needsAttention ? 'History' : guide.tab);
              if (!running && ep?.currentMasterId && ep.status !== 'ACCEPTED') {
                setTimeout(() => {
                  const review = document.getElementById('episode-review');
                  review?.focus({ preventScroll: true });
                  review?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 0);
              }
            }}
          >
            {needsAttention ? 'Review production' : 'Continue'}
            <ArrowRight size={15} />
          </Button>
        </section>
      )}
      <div className="series-tabs">
        {['Overview', 'Blueprint', 'Cast', 'Episodes', 'History'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-current={tab === t ? 'page' : undefined}
            className={tab === t ? 'selected' : ''}
          >
            {t}
            {t === 'Cast' && d.characters.length > 0 && <span>{d.characters.length}</span>}
          </button>
        ))}
      </div>
      {running && (
        <div className="production-banner">
          <LoaderCircle size={18} className="spin" />
          <div>
            <strong>{lab.isDemo ? 'Simulating production…' : workMessage}</strong>
            <span>
              {lab.isDemo
                ? 'No paid API calls are made in this workspace.'
                : needsAttention
                  ? 'Open History for the recorded cause. Completed assets and costs are preserved.'
                  : 'You can close this page. Your production state is saved.'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={!writable || busy}
            onClick={() =>
              void act(() =>
                lab.cancel(d.commands.filter((c) => activeStatuses.includes(c.status)).at(-1)!._id),
              )
            }
          >
            <Pause size={14} />
            Cancel
          </Button>
        </div>
      )}
      {tab === 'Overview' && <OverviewTab />}
      {tab === 'Blueprint' && <BlueprintTab />}
      {tab === 'Cast' && <CastTab />}
      {tab === 'Episodes' && <EpisodesTab />}
      {tab === 'History' && <HistoryTab />}
      {selectedShot && shots.some((shot) => shot._id === selectedShot) && (
        <ShotModal
          key={selectedShot}
          shot={shots.find((s) => s._id === selectedShot)!}
          onClose={() => setSelectedShot(undefined)}
          onError={onError}
          onRegenerate={(shotId, instruction) => run('REGENERATE_SHOT', { shotId, instruction })}
          disabled={controlsDisabled}
        />
      )}
      {showExport && (
        <Modal
          title="Production notes"
          subtitle="Download this series’ versioned creative record."
          onClose={() => setShowExport(false)}
        >
          <p>
            Includes the pitch, bibles, canon, screenplay, and shot specifications. Private media
            and credentials are not included.
          </p>
          <Button
            onClick={() => {
              const blob = new Blob(
                [
                  JSON.stringify(
                    {
                      project: p,
                      bibles: d.bibles,
                      canon: d.canon,
                      episodes: d.episodes,
                      shots: d.shots,
                    },
                    null,
                    2,
                  ),
                ],
                { type: 'application/json' },
              );
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'production-notes.json';
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            <ArrowDownToLine size={16} />
            Download JSON
          </Button>
        </Modal>
      )}
    </SeriesProvider>
  );
}
